import React, { useEffect, useRef, useState } from 'react';
import { MdMap, MdOutlineDiscount } from 'react-icons/md';
import { useRouter } from 'next/router';
import classNames from 'classnames';

import * as toast from 'legacy/common/utils/toast';
import useParams from 'legacy/common/hooks/useParams';
import { useGetMe } from 'legacy/user/hooks';
import {
  getDiscountCodeByCode,
  getVerifyCode,
  useGetEvent,
  useGetTicketInfoRequired,
  useGetTierFromShareTokenCommand
} from 'legacy/events/hooks';
import Container from 'legacy/common/components/layout/Container';
import AlertModal from 'legacy/common/components/modal/AlertModal';
import Title from 'legacy/events/components/event-page/Title';
import About from 'legacy/events/components/event-page/About';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import ScanQRModal from 'legacy/events/components/scanQR/ScanQRModal';
import CoverImage from 'legacy/events/components/event-page/CoverImage';
import EventSkeleton from 'legacy/events/components/event-page/EventSkeleton';
import ContactHostModal from 'legacy/events/components/contact-host/ContactHostModal';
import TicketModalNew from 'legacy/tickets/components/TicketModalNew';
import Background from 'legacy/common/components/wysiwyg-form/Background';
import EventDate from '../components/event/EventDate';
import Divider from 'legacy/common/components/wysiwyg-form/Divider';
import OrganizersBadges from 'legacy/common/components/wysiwyg-form/OrganizersBadges';
import OrganizersGuestsModal from 'legacy/common/components/wysiwyg-form/OrganizersGuestsModal';
import GuestlistBadges from '../components/event/GuestlistBadges';
import EventActionBar from '../components/event-page/EventActionBar';
import EventQuestions from '../components/event/EventQuestions';
import Header from 'legacy/common/components/wysiwyg-form/Header';
import ShareEvent from '../components/event-page/ShareEvent';
import { CodeType, EventForHost } from '../types';
import EventMenu from '../components/event-page/EventMenu';
import MultiTierCheckout from '../components/checkout/MultiTierCheckout';
import MetaPixel, { MetaPixelRef } from '../../common/components/MetaPixel';
import RecurringEventBadge from '../components/event/RecurringEventBadge';
import GuestGridList from '../components/guest-list/GuestGridList';

const EventPage = () => {
  const metaPixelRef = useRef<MetaPixelRef>(null);
  const router = useRouter();
  const params = useParams<{
    slug: string;
    tierShareToken: string;
    invite: string;
    discountCode: string;
    p: string;
  }>();
  const id = params.slug;

  const {
    data: event,
    isLoading: isEventLoading,
    error,
    refetch: refetchEvent
  } = useGetEvent(id, {
    shareToken: params.invite
  });
  const { data: user } = useGetMe();
  const { data: ticketInfoRequired, refetch: refetchTicketInfoRequired } =
    useGetTicketInfoRequired(id);

  const [isCheckoutVisible, setIsCheckoutVisible] = useState(false);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [isContactHostModalOpen, setIsContactHostModalOpen] = useState(false);
  const [isOrganizersModalOpen, setIsOrganizersModalOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState<string | null>(null);
  const [discountCodeError, setDiscountCodeError] = useState<string | null>(
    null
  );
  const [showGuestsModal, setShowGuestsModal] = useState(false);
  const [showScanQRModal, setShowScanQRModal] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isManageEventModalOpen, setIsManageEventModalOpen] = useState(false);
  const [unlockedTier, setUnlockedTier] = useState(null);

  const toggleCheckout = () => {
    setIsCheckoutVisible((isCheckoutVisible) => !isCheckoutVisible);
  };

  const getTierFromShareTokenCommand = useGetTierFromShareTokenCommand();

  const { data: discountCodeData } = getDiscountCodeByCode(id, discountCode);

  /**
   * Checks the validity of the provided discount code whenever we have one.
   */
  const { isSuccess: isCodeVerified } = getVerifyCode(
    id,
    discountCodeData?.code,
    1,
    {
      onSuccess: () => {
        if (discountCodeData?.codeType === CodeType.DiscountCode) {
          toast.success(`Discount code applied: ${discountCodeData?.code}`);
        }
      },
      onError: (error) => {
        setDiscountCode(null);
        setDiscountCodeError(error.message);
      }
    }
  );

  const toggleOrganizersModal = () => {
    setIsOrganizersModalOpen((prevState) => !prevState);
  };

  const viewGuests = () => {
    setShowGuestsModal(true);
  };

  const toggleScanQRModal = () => {
    setShowScanQRModal((showScanQRModal) => !showScanQRModal);
  };

  const toggleShareModal = () => {
    setIsShareModalOpen((shareModalOpen) => !shareModalOpen);
  };

  const toggleManageEvent = () => {
    setIsManageEventModalOpen((manageEventOpen) => !manageEventOpen);
  };

  const toggleViewContactsModal = () => {
    setIsContactHostModalOpen((contactModalOpen) => !contactModalOpen);
  };

  const organizers = {
    groups: event?.groups,
    operators: event?.operators,
    scanners: event?.scanners,
    photographers: event?.photographers,
    cohosts: event?.cohosts,
    host: event?.host
  };

  // Unlock tier if share token is present in URL and if user is logged in
  useEffect(() => {
    const shareToken = params?.tierShareToken;

    if (unlockedTier || !shareToken || !event?._id) {
      return;
    }

    getTierFromShareTokenCommand.mutate(
      {
        eventId: event._id,
        shareToken
      },
      {
        onSuccess: (tier) => {
          setUnlockedTier({
            id: tier.tierId,
            shareToken: params?.tierShareToken
          });

          toast.success(`Private ticket unlocked: ${tier.tierName}`);
        }
      }
    );
  }, [params.tierShareToken, user?._id, event?._id]);

  useEffect(() => {
    const discountCodeParam = params?.p || params?.discountCode;

    if (!discountCodeParam || !event?._id) {
      return;
    }

    setDiscountCode(discountCodeParam);
  }, [params.discountCode, params.p, user?._id, event?._id]);

  if (isEventLoading) {
    return <EventSkeleton />;
  }

  if (error?.message) {
    return (
      <Container>
        <div className="my-20 text-center text-2xl text-gray-700">
          {error.message}
        </div>
      </Container>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <>
      {event?.metaPixelId && (
        <MetaPixel ref={metaPixelRef} pixelId={event.metaPixelId} />
      )}
      <Header
        isDraft={event.isDraft}
        title="Event details"
        onBack={() => {
          router.back();
        }}
        onMenuClick={!event.isScanner && toggleManageEvent}
      />
      <FadeIn>
        <div className="-mt-[10px] md:-mt-0 md:pt-[27px] lg:pt-[57px] pb-20">
          {params.discountCode &&
            discountCodeData?.codeType === CodeType.DiscountCode &&
            isCodeVerified && (
              <div className="z-20 relative h-[50px] flex justify-center align-center bg-green-100 text-green-900 font-normal text-base">
                <MdOutlineDiscount className="mr-1" />
                {discountCodeData.code} applied! Get
                {discountCodeData.discount.kind === 'amount'
                  ? ` $${discountCodeData.discount.value} off`
                  : ` ${discountCodeData.discount.value}% off`}
              </div>
            )}
          {params.p &&
            discountCodeData?.code === params.p &&
            isCodeVerified && (
              <div className="z-20 -mt-[41px] md:-mt-[11px] relative h-[50px] flex justify-center align-center bg-green-100 text-green-900 font-normal text-base gap-1">
                <img
                  src="/img/icons/award-star.svg"
                  alt="Events link"
                  height={18}
                  width={18}
                  className="inline"
                />
                {discountCodeData?.discount ? (
                  <>
                    <strong>{discountCodeData.code}</strong> applied!{' '}
                    {discountCodeData?.discount?.kind === 'amount'
                      ? `Get $${discountCodeData?.discount?.value} off`
                      : `Get ${discountCodeData?.discount?.value}% off`}
                  </>
                ) : (
                  <>
                    <strong>{discountCodeData.code}</strong> applied! Time to
                    lock in your spot!
                  </>
                )}
              </div>
            )}
          {discountCodeError && (
            <div className="z-20 -mt-[41px] md:-mt-[11px] relative h-[50px] flex justify-center align-center bg-red-100 text-red-900 font-normal text-base gap-1">
              <MdOutlineDiscount className="mr-1" />
              {discountCodeError}
            </div>
          )}
          <div
            className={classNames(
              '-mt-[90px] md:-mt-[140px] pb-20 md:w-[630px] md:mx-auto',
              event.seriesId
                ? 'pt-[80px] md:pt-[160px]'
                : 'pt-[90px] md:pt-[180px]'
            )}
          >
            <Background photoUrl={event?.photo?.url} />
            <div className="relative px-4 z-20">
              <div className="flex mx-4 pb-4">
                <RecurringEventBadge
                  eventId={event._id}
                  seriesId={event.seriesId}
                />
              </div>
              <CoverImage photoUrl={event.photo.url} />
              <Title title={event.title} isPrivate={event.private} />
              <div className="pt-4 text-lg font-semibold">{event.address}</div>
              <EventDate
                activeStatus={event.activeStatus}
                startDate={event.startDate}
                endDate={event.endDate}
              />
              {event?.coordinates?.length > 0 && (
                <a
                  className="text-lg font-semibold flex align-center gap-2 mt-4 text-primary-400"
                  target="_blank"
                  href={`https://www.google.com/maps/?q=${event.coordinates[1]},${event.coordinates[0]}`}
                  rel="noreferrer"
                >
                  <MdMap size={20} />
                  See location
                </a>
              )}

              <EventQuestions
                requiredInfo={ticketInfoRequired}
                event={event}
                refetchTicketInfoRequired={refetchTicketInfoRequired}
              />

              <OrganizersBadges
                onClick={toggleOrganizersModal}
                isEditable={false}
                data={organizers}
              />
              {event.guestPreview &&
                (event.isOrganizer || event.isScanner || event.showGuests) && (
                  <GuestlistBadges
                    guestsPreview={event.guestPreview}
                    totalGuests={event.totalGuests}
                    onClick={viewGuests}
                  />
                )}

              <Divider />
              <About description={event.description} />

              {/* Modals */}
              <OrganizersGuestsModal
                data={organizers}
                isOpen={isOrganizersModalOpen}
                onClose={toggleOrganizersModal}
              />
              {showGuestsModal && (
                <GuestGridList
                  eventId={event?._id}
                  onClose={() => {
                    setShowGuestsModal(false);
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <EventActionBar
          event={event}
          toggleManageEvent={toggleManageEvent}
          toggleScanQRModal={toggleScanQRModal}
          toggleShareModal={toggleShareModal}
          user={user}
          setIsTicketModalOpen={setIsTicketModalOpen}
          toggleViewContactsModal={toggleViewContactsModal}
          toggleCheckout={toggleCheckout}
        />
      </FadeIn>

      {isCheckoutVisible && (
        <MultiTierCheckout
          metaPixelRef={metaPixelRef}
          event={event}
          onClose={toggleCheckout}
          unlockedTier={unlockedTier}
          defaultDiscountCode={
            discountCodeData?.codeType === CodeType.DiscountCode
              ? discountCode
              : undefined
          }
          defaultPromoterCode={
            discountCodeData?.codeType === CodeType.PromoterCode
              ? discountCode
              : undefined
          }
          onSuccess={() => {
            refetchEvent();
            refetchTicketInfoRequired();
          }}
        />
      )}

      {getTierFromShareTokenCommand.error?.message && (
        <AlertModal
          onSubmit={getTierFromShareTokenCommand.reset}
          message={getTierFromShareTokenCommand.error.message}
        />
      )}

      {isTicketModalOpen && (
        <TicketModalNew
          eventId={event?._id}
          onClose={() => setIsTicketModalOpen(false)}
          unlockedTier={unlockedTier}
          discountCode={
            discountCodeData?.codeType === CodeType.DiscountCode
              ? discountCode
              : undefined
          }
          promoterCode={
            discountCodeData?.codeType === CodeType.PromoterCode
              ? discountCode
              : undefined
          }
        />
      )}

      {isContactHostModalOpen && user && (
        <ContactHostModal
          eventId={event._id}
          onClose={() => {
            setIsContactHostModalOpen(false);
          }}
        />
      )}

      {showScanQRModal && (
        <ScanQRModal
          eventId={event._id}
          onClose={() => {
            setShowScanQRModal(false);
          }}
        />
      )}

      <ShareEvent
        eventId={event._id}
        isPrivate={event.private}
        isOrganizer={event.isOrganizer}
        shareToken={(event as EventForHost).shareToken}
        visible={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />

      <EventMenu
        event={event}
        onClickGuestPreview={viewGuests}
        toggleCheckout={toggleCheckout}
        onClickViewShareOptions={toggleShareModal}
        toggleManageEvent={toggleManageEvent}
        onClickViewContactsModal={toggleViewContactsModal}
        visible={isManageEventModalOpen}
        isLoggedIn={Boolean(user)}
      />
    </>
  );
};

export default EventPage;
